import { createContext, useCallback, useContext, useEffect, useMemo, useState, PropsWithChildren } from 'react'
import { Lang, SUPPORTED_LANGS, Translations } from '.'
import pt from './locales/pt'
import en from './locales/en'
import es from './locales/es'
import { detectInitialLang, resolveLang, storeLang } from './detectLanguage'

const dictionaries: Record<Lang, Translations> = { pt, en, es }

interface I18nContextValue {
  lang: Lang
  setLang: (lang: Lang) => void
  t: Translations
}

const I18nContext = createContext<I18nContextValue | null>(null)

export function useTranslation(): I18nContextValue {
  const ctx = useContext(I18nContext)
  if (!ctx) {
    throw new Error('useTranslation must be used inside <I18nProvider>')
  }
  return ctx
}

function applyHtmlLang(lang: Lang): void {
  const htmlLang = dictionaries[lang].meta.htmlLang
  if (document.documentElement.lang !== htmlLang) {
    document.documentElement.lang = htmlLang
  }
  // Belt-and-suspenders: keep `translate="no"` on the root so Chrome/Edge/Brave/Mozilla
  // never offer to auto-translate. The initial HTML already carries this attribute, but
  // we re-apply here in case some other script removes it.
  document.documentElement.setAttribute('translate', 'no')
}

export function I18nProvider({ children }: PropsWithChildren) {
  const [lang, setLangState] = useState<Lang>(() => detectInitialLang())

  useEffect(() => {
    applyHtmlLang(lang)
  }, [lang])

  // On first mount, refine the language using geolocation (which has
  // priority over the browser language per product spec). If the user
  // has a stored choice, `resolveLang` returns it immediately.
  useEffect(() => {
    let cancelled = false
    resolveLang().then((resolved) => {
      if (cancelled) return
      setLangState((current) => (current === resolved ? current : resolved))
    })
    return () => {
      cancelled = true
    }
  }, [])

  const setLang = useCallback((next: Lang) => {
    if (!SUPPORTED_LANGS.includes(next)) return
    storeLang(next)
    setLangState(next)
  }, [])

  const value = useMemo<I18nContextValue>(
    () => ({ lang, setLang, t: dictionaries[lang] }),
    [lang, setLang]
  )

  return <I18nContext.Provider value={value}>{children}</I18nContext.Provider>
}
